import { Controller } from "@hotwired/stimulus"
import { t } from "../i18n.js"

// Real reader input. Reaching the end of a document is only meaningful once a
// human has actually touched the page: a session resolved by a bare GET (an
// email scanner, a link-preview fetcher) must never look like reading — the
// same bar ReaderSession#meaningfully_engaged? holds the server side to.
const ENGAGEMENT_EVENTS = ["scroll", "wheel", "keydown", "pointerdown", "touchstart", "mousemove"]

// How long after connect() a scroll event is still assumed to be our own
// restorePosition() call rather than the reader moving the page themselves.
const PROGRAMMATIC_SCROLL_GRACE_MS = 600

// A position is resume/progress evidence, not the completion signal. Waiting
// for a short pause keeps resume accuracy at paragraph granularity while
// avoiding a PATCH for every brief hesitation during an ordinary scroll.
const POSITION_SAVE_DEBOUNCE_MS = 750

export default class extends Controller {
  static targets = [
    "anchor", "progressBar", "progressFill", "progressLabel", "completion",
    "endSentinel", "endAffordance", "reachedEndButton", "completeButton",
    "bookmarkSummaryMarked", "bookmarkSummaryLink", "bookmarkSummaryMeta",
    "bookmarkSummaryUnreached", "bookmarkSummaryEmpty"
  ]
  static values = {
    position: Number, total: Number, url: String, resumeAnchor: String,
    bookmarkUrl: String, completeUrl: String, completed: Boolean, checkpointed: Boolean
  }

  connect() {
    this.engaged = false
    this.endReached = false
    this.endRevealed = false
    this.programmaticScrollUntil = 0

    this.restorePosition()

    this.onScroll = this.trackPosition.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })

    this.onEngagement = this.registerEngagement.bind(this)
    ENGAGEMENT_EVENTS.forEach(name => {
      window.addEventListener(name, this.onEngagement, { passive: true })
    })

    this.observeEnd()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    ENGAGEMENT_EVENTS.forEach(name => window.removeEventListener(name, this.onEngagement))
    this.endObserver?.disconnect()
    window.clearTimeout(this.saveTimer)
  }

  // ---- end of document ---------------------------------------------------

  // Watches an empty sentinel placed immediately after the readable content.
  // Position geometry cannot answer "did the reader reach the end?": a short
  // document never scrolls, so no scroll handler ever runs, and on a long one
  // the final paragraph is at the *bottom* of the viewport when the reader
  // arrives, so it never becomes the paragraph nearest the top. The sentinel
  // becoming visible is the signal; paragraph positions stay what they always
  // were — a resume point and a progress readout.
  observeEnd() {
    if (this.completedValue || !this.hasCompleteUrlValue || !this.hasEndSentinelTarget) return
    if (typeof IntersectionObserver === "undefined") return

    this.endObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return

      this.endReached = true
      this.revealCompletionControl()
    })
    this.endObserver.observe(this.endSentinelTarget)
  }

  // The first genuine interaction is what turns a resolved session into a
  // reading one — including for a document that fits entirely inside the
  // viewport, where the sentinel is already visible on load and would
  // otherwise let a bare page fetch look like reading. It no longer follows
  // that reaching the end completes the document; see revealCompletionControl.
  registerEngagement(event) {
    if (this.engaged) return
    if (event?.type === "scroll" && Date.now() < this.programmaticScrollUntil) return

    this.engaged = true
    ENGAGEMENT_EVENTS.forEach(name => window.removeEventListener(name, this.onEngagement))

    this.saveClosestPosition().then(() => this.revealCompletionControl())
  }

  // Reaching the end sentinel is progress, never completion, by itself —
  // the same distinction ReaderSession#record_reading_completion! enforces
  // server-side (it refuses to complete a session that hasn't already
  // recorded the final position through a separate call). Once a genuinely
  // engaged reader reaches the end, this records that final position —
  // exactly what the no-JS "I've reached the end" button does — and swaps
  // it out for the real completion control. It never submits completion on
  // the reader's behalf: "Mark finished" is a plain form post with no JS
  // interception (see _end_of_document.html.erb), so finishing the document
  // always requires that separate, explicit click, whether or not this ran.
  async revealCompletionControl() {
    if (!this.engaged || !this.endReached) return
    if (this.completedValue || this.endRevealed || !this.hasCompleteUrlValue) return

    this.endRevealed = true
    try {
      await this.recordFinalPosition()
      if (this.hasReachedEndButtonTarget) this.reachedEndButtonTarget.hidden = true
      if (this.hasCompleteButtonTarget) this.completeButtonTarget.hidden = false
    } catch (error) {
      // Leaving `endRevealed` false lets a later intersection retry, and the
      // server-rendered "I've reached the end" control is still on the page.
      this.endRevealed = false
      console.error(error)
    }
  }

  // The completion endpoint never creates its own prerequisite progress. Once
  // the engaged reader reaches the end sentinel, record the final visible
  // position through the ordinary progress endpoint first, then request the
  // separate completion transition. This is server-observed interaction, not
  // a claim that the browser can prove attention or comprehension.
  async recordFinalPosition() {
    const finalPosition = this.totalValue - 1
    if (finalPosition < 0) throw new Error("Document has no readable positions")

    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ position: finalPosition })
    })
    if (!response.ok) throw new Error(`Final position failed with ${response.status}`)

    this.positionValue = finalPosition
    this.updateProgress(finalPosition)
  }

  applyCompletion(result) {
    this.completedValue = true
    this.endObserver?.disconnect()

    if (result.progress_percentage !== undefined) this.renderProgress(result.progress_percentage)
    if (this.hasCompletionTarget && result.completion_html) {
      this.completionTarget.innerHTML = result.completion_html
    }
    if (this.hasEndAffordanceTarget) this.endAffordanceTarget.remove()

    // Reaching the end is a side effect of scrolling, not a direct click, so
    // this announces rather than steals focus from wherever the reader is —
    // moving focus here would be a surprising context change mid-scroll.
    this.announce("Document complete.")
  }

  // Prefers the exact sentence-level resume anchor (set by double-clicking a
  // sentence) over the paragraph-level fallback, and gives it a subtle,
  // transient highlight rather than the old confirm-modal interruption.
  restorePosition() {
    if (this.hasResumeAnchorValue && this.resumeAnchorValue) {
      const el = document.getElementById(this.resumeAnchorValue)
      if (el) {
        this.scrollWithoutEngaging(el, { block: "center" })
        this.flashResume(el)
        return
      }
    }

    if (!this.hasPositionValue) return

    const anchor = document.getElementById(`author-document-position-${this.positionValue}`)
    if (anchor) this.scrollWithoutEngaging(anchor)
  }

  // Restoring a reader's place scrolls the page, and a scroll event is
  // otherwise our strongest signal that a human is here. Discount our own.
  scrollWithoutEngaging(element, options = {}) {
    this.programmaticScrollUntil = Date.now() + PROGRAMMATIC_SCROLL_GRACE_MS
    element.scrollIntoView(options)
  }

  flashResume(el) {
    el.classList.add("reader-sentence--resume-flash")
    window.setTimeout(() => el.classList.remove("reader-sentence--resume-flash"), 2500)
  }

  // Double-clicking a sentence marks the reader's exact place: it becomes
  // the resume anchor and a bookmark, refined to this sentence if the
  // paragraph was already bookmarked. Does not touch last_read_position —
  // that stays the monotonic, scroll-tracked furthest-progress value.
  //
  // Sentence spans are plain, non-focusable text (deliberately — hundreds of
  // tab stops through ordinary prose would be worse than no keyboard access
  // at all), so dblclick has no keyboard equivalent at that granularity.
  // markParagraph below is the keyboard path: same mark, paragraph-grained,
  // reached through the already-focusable per-paragraph rail.
  markSentence(event) {
    const el = event.currentTarget
    const id = el.dataset.sentId
    const match = id?.match(/^p_(\d+)_s_(\d+)$/)
    if (!match) return

    const position = Number(match[1])
    const excerpt = el.textContent.trim().slice(0, 160)
    this.mark(position, id, excerpt, el)
  }

  markParagraph(event) {
    const position = Number(event.params.position)
    if (Number.isNaN(position)) return

    const id = `p_${position}_s_0`
    const el = document.getElementById(id)
    const excerpt = (el?.textContent || "").trim().slice(0, 160)
    this.mark(position, id, excerpt, el)
  }

  async mark(position, id, excerpt, el) {
    if (!this.hasBookmarkUrlValue) return

    try {
      const response = await fetch(this.bookmarkUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ position, anchor_id: id, excerpt })
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || "Couldn't mark this spot. Please try again.")
      }

      this.applyMark(id, el, excerpt)
    } catch (error) {
      console.error(error)
      window.alert(error.message || t('bookmark_failed', "Couldn't mark this spot. Please check your connection and try again."))
    }
  }

  applyMark(id, el, excerpt) {
    this.resumeAnchorValue = id

    document.querySelectorAll(".reader-sentence--marked").forEach(marked => {
      marked.classList.remove("reader-sentence--marked")
    })

    if (el) {
      this.flashResume(el)
      el.classList.add("reader-sentence--marked")
    }

    this.updateBookmarkSummary(id, excerpt)
    this.announce(excerpt ? `Marked "${excerpt}" as your reading position.` : "Marked your reading position.")
  }

  updateBookmarkSummary(id, excerpt) {
    if (this.hasBookmarkSummaryLinkTarget) {
      this.bookmarkSummaryLinkTarget.setAttribute("href", `#${id}`)
      this.bookmarkSummaryLinkTarget.textContent = `“${excerpt}”`
    }
    if (this.hasBookmarkSummaryMetaTarget) this.bookmarkSummaryMetaTarget.textContent = t('marked_now', " — marked just now")
    if (this.hasBookmarkSummaryMarkedTarget) this.bookmarkSummaryMarkedTarget.hidden = false
    if (this.hasBookmarkSummaryUnreachedTarget) this.bookmarkSummaryUnreachedTarget.hidden = true
    if (this.hasBookmarkSummaryEmptyTarget) this.bookmarkSummaryEmptyTarget.hidden = true
  }

  trackPosition() {
    window.clearTimeout(this.saveTimer)
    this.saveTimer = window.setTimeout(() => this.saveClosestPosition(), POSITION_SAVE_DEBOUNCE_MS)
  }

  // Resolves once the position is recorded (or once there is nothing to
  // record), so the caller can order a follow-up request behind it.
  async saveClosestPosition() {
    const visible = this.anchorTargets.reduce((closest, anchor) => {
      const distance = Math.abs(anchor.getBoundingClientRect().top)
      return distance < closest.distance ? { anchor, distance } : closest
    }, { anchor: null, distance: Number.POSITIVE_INFINITY }).anchor

    if (!visible) return
    const position = Number(visible.dataset.position)
    if (this.hasPositionValue && position === this.positionValue) return

    this.positionValue = position
    this.updateProgress(position)

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ position })
      })
      if (!response.ok || response.status === 204) return

      const result = await response.json()
      if (result.progress_percentage !== undefined) this.renderProgress(result.progress_percentage)
      if (result.completed) this.applyCompletion(result)
    } catch (error) {
      console.error(error)
    }
  }

  // Checkpointed reading's progress bar is authorization state, not a scroll
  // readout — it may only move when the server actually advances a
  // checkpoint (see reader_checkpoint_sync_controller.js). Rendering a raw
  // scroll-through-total-length estimate here for a checkpointed document
  // would show progress beyond what's actually been authorized, and that
  // estimate would then visibly snap back down on refresh once the
  // server re-renders the true, lower checkpoint percentage.
  updateProgress(position) {
    if (this.checkpointedValue) return
    if (!this.hasTotalValue || this.totalValue <= 0) return
    this.renderProgress(Math.min(100, Math.round(((position + 1) * 100) / this.totalValue)))
  }

  renderProgress(percentage) {
    if (this.hasProgressLabelTarget) this.progressLabelTarget.textContent = `${percentage}%`
    if (this.hasProgressFillTarget) this.progressFillTarget.style.width = `${percentage}%`
    if (this.hasProgressBarTarget) this.progressBarTarget.setAttribute("aria-valuenow", percentage)
  }

  // A shared sr-only aria-live region for state changes that happen without
  // a full page reload (a scroll-triggered completion, a mark, a failed
  // fetch) — updates a sighted mouse user already sees on screen, but that a
  // screen-reader user gets no other signal of. Checkpoint/acknowledge
  // transitions go through reader_focus_transitions.js instead, since those
  // reload the page.
  announce(message) {
    const live = document.getElementById("reader-live-status")
    if (live) live.textContent = message
  }
}
